/**
 * Open and click tracking — the switch, and the sentence that has to be true
 * either side of it.
 *
 * ## This card is mostly copy, and the copy is the deliverable
 *
 * Slices 4 and 5 wrote down a property and were right to: *an email Wend sends
 * makes no network request when it is opened.* MJML's Google Fonts `<link>` was
 * removed for it; inline images became CID parts for it. That sentence stops
 * being unconditionally true the moment a tenant switches this on, because SES
 * injects a 1×1 pixel into the HTML and rewrites every link through a redirect
 * — **server-side, after Wend has handed the message over**, so the bytes this
 * codebase composes are unchanged and the promise is broken anyway.
 *
 * So this card states the conditional truth, and states it from whichever side
 * the tenant is actually on. With tracking off it prints the old promise in
 * full, because a tenant who has kept it is entitled to see it stated rather
 * than to see nothing — **where the deployment embeds pictures**. Where it hosts
 * them (slice 21), an email with a picture fetches it from Wend's image host
 * when it is opened, so the API sends the tracking-off sentence that says so
 * (`trackingOffDisclosure`), and this card prints whichever it was given. With
 * tracking on it prints what is in their mail instead. Neither sentence is
 * written here: both arrive on the wire, from `TRACKING_DISCLOSURE` and
 * `trackingOffDisclosure`, so the copy on this screen and the text the API writes
 * into its audit entry are provably the same words, whichever frontend build
 * happens to be deployed.
 *
 * The switch itself must say, **before** anybody turns it on, exactly what
 * turning it on does to their recipients: an invisible image that reports when
 * a message is opened, and every link rewritten to pass through a redirect.
 * Not "enables engagement analytics". A tenant mailing cooperative banks may
 * have promised somebody that they do not track, and this screen is where they
 * find out whether they can keep that promise. `DisclosedSwitch` is the
 * component that makes that structurally true rather than a matter of what
 * somebody remembered to write.
 *
 * ## Where this card now sits, and what that did not change
 *
 * Slice 16 moved it. It used to be the second of three cards stacked down
 * `/integrations`; it is now inside the **Advanced** disclosure on the Email
 * tab, collapsed by default, together with the redirect domain, VDM and the SNS
 * topic panel that carries the webhook URL. The dividing line is *does this
 * affect whether you can send?* — the SES sandbox does, and stays above the
 * fold; none of this does.
 *
 * **Not one word of the copy moved with it.** The disclosure above each switch
 * is the reason this file exists, a tenant may have promised somebody that they
 * do not track, and "it had to fit behind a disclosure" is not a reason to
 * shorten a sentence somebody's promise depends on. Nothing here became
 * hover-only either. Nor did anything become conditional: `AdvancedDisclosure`
 * is a `<details>`, so this card and its panels mount with the page exactly as
 * they did when it was the second of three stacked cards. Collapsing the
 * section does not throw away a half-typed SNS topic ARN, and find-in-page
 * still reaches a stored value through a closed disclosure.
 *
 * The old placement argument stands as the superseded position and is still the
 * reason this is a card of its own rather than a section of the SES card: the
 * SES card answers "can this account send at all" and this one answers "what
 * does it put in the message", two questions with different repairs.
 *
 * **The controls moved; the answer did not get to move with them.** Collapsing
 * this card put *whether this workspace tracks* inside a `<details>` whose
 * closed summary is a constant string — identical for a workspace that pixels
 * every recipient and one that pixels none. That is not a control being tidied
 * away, it is a fact about what every recipient receives becoming invisible, and
 * the tenant most entitled to see it is the one who promised somebody it was
 * false. So the state is reported upward through {@link TrackingCardProps} and
 * the Email tab prints one sentence of it above the fold, while every switch
 * stays exactly where this slice put it. The sentence is `trackingStateLine`'s,
 * beside the badge, so the two surfaces are one derivation and the long form
 * below is the one that wins if they ever read differently.
 *
 * ## Preconditions are shown once, with the button that fixes them
 *
 * Two things must be true before either switch means anything: an SES account
 * must be connected, and Wend must have created the configuration set, its
 * event destination and its tracking options inside that account. Amazon emits
 * open and click events **only** through a configuration set's event
 * destination — there is no identity-level route to them — so tracking switched
 * on without one would show a tenant zeroes for ever.
 *
 * When either is missing this card renders the reason once, with the action
 * that repairs it, and renders no switches at all. The alternative — every
 * switch and both panels each carrying the same refusal — puts the identical
 * paragraph on the screen five times and buries the one button that does
 * anything. `needs_configuration_set` in particular is **Wend's** failure and
 * not the tenant's, which is why the repair is a button here and not an
 * instruction to go and do something in the AWS console.
 *
 * ## Wend provisions it; the tenant does not
 *
 * That is decision 1 of this slice, and the argument is the one DKIM already
 * made: a setup step a tenant performs by hand fails silently and surfaces as
 * "why are there no numbers" three weeks later. The set, its event destination
 * and its tracking domain are created as one unit that either works or visibly
 * does not — and "visibly" is this card's job.
 *
 * **With exactly one exception, and it is a deliberate one.** The event
 * destination has to publish to an SNS topic, and Wend does not create that —
 * creating it would mean holding `sns:CreateTopic` and `sns:Subscribe` over a
 * customer's AWS account for a step most tenants performed in slice 9. So the
 * tenant nominates the topic and Wend builds everything downstream of it. That
 * does not reopen decision 1, because a missing topic does not fail silently:
 * the API refuses provisioning outright and names the missing input. It did,
 * however, name a field that this screen did not have until `SnsTopicPanel` was
 * written — the refusal ends "paste its ARN here" and there was no *here*, so
 * the one repair path on this card dead-ended in a paragraph. That panel is the
 * fix, and its header carries the detail.
 *
 * **Slice 19 widened what the topic is for.** Saving it now sets up delivery
 * reports — a second configuration set that untracked email goes through and
 * that adds nothing to a message — so the panel states whether they are set up,
 * with its own button, whatever the switches on this card say.
 *
 * ## What this card cannot do, and does not pretend to
 *
 * Nothing here talks to AWS. Every button is a call to Wend's API, which holds
 * the tenant's sealed credential and makes the provider call on its own side.
 * And nothing here decides whether a `SendEmailCommand` carries a
 * `ConfigurationSetName` — that is `configurationSetNameForSend()` in
 * `@wend/shared`, read by the transport, and the safety property of this slice
 * lives there rather than in anything a screen can get wrong.
 */
import { type TrackingResponse, type TrackingSettingsUpdate } from '@wend/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Kicker,
  Spinner,
} from '@wend/ui';
import { MousePointerClick, Wrench } from 'lucide-react';
import { useEffect, useState } from 'react';

import { ErrorState } from '../../components/ErrorState';
import { FormAlert } from '../../components/FormAlert';
import { errorMessage } from '../../lib/api/errors';
import { formatDateTime } from '../../lib/format';
import { useAsyncData } from '../../lib/use-async-data';
import { DisclosedSwitch } from './DisclosedSwitch';
import { RedirectDomainPanel } from './RedirectDomainPanel';
import { SnsTopicPanel } from './SnsTopicPanel';
import { VdmPanel } from './VdmPanel';
import { RequestFailureAlert, requestFailure, type RequestFailure } from './request-failure';
import { fetchTracking, provisionTracking, saveTracking } from './tracking-api';
import {
  readTracking,
  trackingStateLine,
  unchangedUpdate,
  wouldAllow,
  type TrackingStateLine,
} from './tracking-display';

export interface TrackingCardProps {
  /**
   * Called with the one-sentence state whenever this card learns it, and with
   * `null` while it does not know — a load still in flight, or one that failed.
   *
   * **The Email tab renders that sentence above the fold, and this callback is
   * how it stays one derivation rather than two.** The tab has no tracking
   * request of its own: a second `GET /tracking` would put two accounts of what
   * is inside a tenant's mail a few centimetres apart, and they would disagree
   * the moment somebody flicked a switch — the panel repainting from the write's
   * own response while the line above it still showed the load. Same seam, same
   * argument, as a panel reporting its badge to the tab rail.
   *
   * `null` is a real value and never a healthy default: "nothing known" and
   * "nothing is added to your messages" are different statements, and only one of
   * them is ever a promise a tenant can rely on.
   */
  readonly onStateChange?: (state: TrackingStateLine | null) => void;
}

export function TrackingCard({ onStateChange }: TrackingCardProps) {
  const { data, error, loadingFirst, refreshing, reload } = useAsyncData(
    async (signal) => fetchTracking(signal),
    [],
  );

  /**
   * The response as this card has changed it since the server last spoke.
   *
   * Every write on this card answers with the whole of `TrackingResponse`, so
   * this is a replacement rather than a merge — see `tracking-api.ts` for why
   * the routes are shaped that way. It is cleared whenever a fresh load
   * arrives, so the server always wins in the end.
   */
  const [patched, setPatched] = useState<TrackingResponse | null>(null);
  useEffect(() => {
    setPatched(null);
  }, [data]);

  const current = patched ?? data;

  /*
   * The report is keyed on the two booleans rather than on the response, and it
   * is computed here rather than inside `TrackingBody`. Both are deliberate. The
   * response is a fresh object on every write, so an effect keyed on it would
   * fire for a redirect-domain check that changed nothing a recipient sees; and
   * `TrackingBody` is not mounted while the card is loading or has failed, which
   * is exactly when the tab above has to be told that nothing is known yet.
   */
  const open = current?.settings.open ?? null;
  const click = current?.settings.click ?? null;
  useEffect(() => {
    if (onStateChange === undefined) return;
    onStateChange(open === null || click === null ? null : trackingStateLine({ open, click }));
  }, [onStateChange, open, click]);

  if (loadingFirst) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3 px-6 py-12 text-[0.92rem] text-muted">
          <Spinner size="md" label={null} />
          <span role="status">Reading your tracking settings…</span>
        </CardContent>
      </Card>
    );
  }

  if (current === null) {
    return (
      <Card>
        <ErrorState error={error} title="Could not load your tracking settings" onRetry={reload} />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {error !== null && (
        <FormAlert tone="error">
          {errorMessage(error)} What is shown below is the last version that loaded.
        </FormAlert>
      )}
      <TrackingBody response={current} refreshing={refreshing} onChanged={setPatched} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The card                                                                    */
/* -------------------------------------------------------------------------- */

function TrackingBody({
  response,
  refreshing,
  onChanged,
}: {
  response: TrackingResponse;
  refreshing: boolean;
  onChanged: (next: TrackingResponse) => void;
}) {
  const reading = readTracking(response);
  const { settings } = response;

  const [busy, setBusy] = useState(false);
  const [provisionFailure, setProvisionFailure] = useState<RequestFailure | null>(null);
  const [provisioning, setProvisioning] = useState(false);

  /**
   * The one write path for the three switches.
   *
   * It does not catch. `DisclosedSwitch` owns the failure that belongs beside
   * the control the person just used, and swallowing it here would show a
   * refusal in a place they were not looking. `busy` is set so that the *other*
   * controls on the card go quiet while one of them is writing — three
   * concurrent whole-body PUTs would race over the same three booleans.
   */
  async function save(update: TrackingSettingsUpdate): Promise<void> {
    setBusy(true);
    try {
      onChanged(await saveTracking(update));
    } finally {
      setBusy(false);
    }
  }

  async function provision(): Promise<void> {
    if (provisioning) return;
    setProvisionFailure(null);
    setProvisioning(true);
    try {
      onChanged(await provisionTracking());
    } catch (error) {
      setProvisionFailure(requestFailure(error));
    } finally {
      setProvisioning(false);
    }
  }

  /**
   * Whether click tracking may be switched on, asked of the shared evaluator
   * rather than answered here.
   *
   * `evaluateTrackingChange` is what the API runs to decide whether the write
   * happens; running the same function to decide whether the control is offered
   * is what stops the two from drifting — and the way they drift is that one of
   * them stops refusing. Its refusal message is what the switch shows, so the
   * sentence a person reads and the sentence the API would log are one string.
   */
  const clickDecision = wouldAllow(settings, response.sesConnected, {
    ...unchangedUpdate(settings),
    click: true,
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <span className="grid size-8 shrink-0 place-items-center rounded-base border border-line bg-white/[0.04] text-muted">
            <MousePointerClick aria-hidden="true" className="size-4" />
          </span>
          <Kicker as="span">
            Email · Tracking
          </Kicker>
          <Badge tone={reading.badge.tone}>{reading.badge.label}</Badge>
          {(refreshing || busy) && <Spinner size="sm" label="Saving" />}
        </div>
        <CardTitle>Open and click tracking</CardTitle>
        <CardDescription>
          Off unless you switch it on, and off is the state Wend ships in. Switching it on changes
          what is inside the messages your recipients receive, which is why this page says exactly
          what changes before it offers you the switch.
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        {/*
          Slice 4's sentence, in whichever form is currently true. Verbatim from
          the wire either way — see the file header for why this is the one
          paragraph on the screen that is not allowed to be paraphrased.
        */}
        {reading.tracks ? (
          <Callout tone="info" title="Your messages carry tracking">
            <p>
              {settings.open && settings.click
                ? 'Every HTML message this workspace sends carries an invisible 1×1 image, and every link in it is rewritten to pass through a redirect first.'
                : settings.open
                  ? 'Every HTML message this workspace sends carries an invisible 1×1 image. Links are not rewritten.'
                  : 'Every link in your messages is rewritten to pass through a redirect first. No tracking image is added.'}{' '}
              Amazon does this on its side after Wend hands the message over, so it applies to
              everything sent from now on and to nothing sent before.
            </p>
            {settings.updatedAt !== null && (
              <p className="mt-2 font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
                Changed {formatDateTime(settings.updatedAt)}
              </p>
            )}
          </Callout>
        ) : (
          <Callout tone="good" title="Nothing is added to your messages">
            {/* `trackingOffDisclosure(imageDelivery)`, chosen by the API: the promise slices 4 and 5 earned where pictures are embedded, and the version that admits hosted pictures are fetched where they are not. */}
            {response.disclosure.off}
          </Callout>
        )}

        {provisionFailure !== null && <RequestFailureAlert failure={provisionFailure} />}

        {reading.blockedBy !== null && (
          /*
            One precondition, one paragraph, one button. When tracking is off,
            this replaces the switches entirely — a control whose every outcome
            is the same refusal is a control that hides the sentence that
            matters underneath four copies of itself.
          */
          <Callout
            tone="warn"
            title={
              response.sesConnected
                ? 'Tracking is not set up in your AWS account yet'
                : 'Connect Amazon SES first'
            }
            action={
              response.sesConnected ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={provisioning}
                  leadingIcon={<Wrench aria-hidden="true" />}
                  onClick={() => {
                    void provision();
                  }}
                >
                  {provisioning ? 'Setting it up in AWS' : 'Set up tracking in my AWS account'}
                </Button>
              ) : undefined
            }
          >
            <p>{reading.blockedBy}</p>
            {response.sesConnected && (
              <p className="mt-2">
                Wend creates this for you — a configuration set, one event destination pointed back
                at Wend, and the tracking options — rather than asking you to build it in the AWS
                console, because a setup step done by hand fails quietly and surfaces weeks later as
                an empty chart. The same press sets up delivery reports in a second configuration
                set, which Wend stops using if anything on it would add to a message. It is safe to
                press twice: both sets are
                named after this workspace, so a run that failed halfway is finished by the next one
                rather than leaving a second copy behind in your account.
              </p>
            )}
          </Callout>
        )}

        {/*
          THE SNS TOPIC, whenever there is an AWS account for one to live in.

          Gated on `sesConnected` alone rather than on the provisioning state,
          because the browser cannot see whether a topic is nominated —
          `TrackingResponse` does not carry `snsTopicArn`, the controller having
          stripped it — and because this is the input the provisioning failure
          above literally tells somebody to come and fill in. Rendering it only
          after that failure would put the field behind the error that exists
          because the field was missing.

          It sits above the switches and outside their gate: nominating a topic
          changes nothing about anybody's mail, so it is not a decision about
          recipients and does not belong among the controls that are. (Since
          slice 19 it sets up delivery reports, which route untracked mail
          through a configuration set that adds nothing to it — still not a
          decision about what a recipient receives.)
        */}
        {response.sesConnected && (
          <SnsTopicPanel
            onChanged={onChanged}
            busy={busy || provisioning}
            unprovisioned={!settings.configurationSet.provisioned}
            deliveryReports={settings.deliveryReports}
          />
        )}

        {/*
          THE SWITCHES ARE SHOWN WHENEVER TRACKING IS ON, EVEN WHEN A
          PRECONDITION IS MISSING — and that is not a softening of the gate, it
          is the gate not being allowed to trap somebody.

          A CORRECTION, because this comment previously argued the point from a
          fact that is not true. It said that disconnecting SES leaves the
          tracking settings behind, so a workspace could sit at `open: true,
          sesConnected: false`. It cannot. Migration 0014 puts `tracking` on the
          `integrations` row itself, `disconnectSes` deletes that row, and
          `TrackingService.read` answers `TRACKING_OFF` with `sesConnected:
          false` when there is no row — so `sesConnected: false` always arrives
          with tracking already off, and `reading.tracks` is false there. The
          old reasoning described a state the data model makes unreachable.

          The branch stays, for the reachable half of the same argument.
          `blockedBy` is also raised by `needs_configuration_set`, which is a
          property of the AWS account rather than of the row: a configuration
          set can stop being provisioned while `open` is still stored true — a
          reconcile that fails, a set deleted in the AWS console — and in that
          state SES is connected, `reading.tracks` is true, and gating the
          switches would leave somebody with tracking recorded as on, this card
          saying so, and no control that turns it off.

          Turning tracking off is never refused, and that is what makes this
          safe rather than optimistic: `evaluateTrackingChange` checks the
          connection and the configuration set only when the request *wants*
          tracking (`wantsTracking = requested.open || requested.click`), so
          `{ open: false, click: false }` is allowed from any state whatsoever.
          The way out always exists, and this is where it is offered.
        */}
        {(reading.blockedBy === null || reading.tracks) && (
          <>
            <div className="flex flex-col">
              <DisclosedSwitch
                title="Open tracking"
                on={settings.open}
                // Verbatim: an invisible 1×1 image, what loads it, and the fact
                // that the resulting count is wrong in both directions.
                disclosure={response.disclosure.open}
                turnOnLabel="Turn on open tracking"
                confirmLabel="Yes, add a tracking pixel to my messages"
                confirmNote={
                  <p>
                    Every HTML message this workspace sends from now on will carry an invisible 1×1
                    image, and Amazon will record an open each time a mail app loads it. Messages
                    already delivered are unchanged, and nothing is measured retroactively.
                  </p>
                }
                turnOffLabel="Turn off open tracking"
                // The precondition, when there is one. Ignored while the switch
                // is on, because then the only thing it offers is the way out.
                refusal={reading.blockedBy}
                busy={busy || provisioning}
                onChange={async (next) => {
                  await save({ ...unchangedUpdate(settings), open: next });
                }}
              />

              <DisclosedSwitch
                title="Click tracking"
                on={settings.click}
                // Verbatim: every link rewritten, and the hover address the
                // recipient actually sees.
                disclosure={response.disclosure.click}
                turnOnLabel="Turn on click tracking"
                confirmLabel="Yes, rewrite every link in my messages"
                confirmNote={
                  <p>
                    Every link in every message this workspace sends from now on will be rewritten
                    to pass through{' '}
                    {settings.redirectDomain.state === 'verified' ? (
                      <span className="font-mono text-[0.88rem]">
                        {settings.redirectDomain.domain}
                      </span>
                    ) : (
                      'your redirect subdomain'
                    )}{' '}
                    first. That is the address recipients will see when they hover a link, and it is
                    what they will see in the status bar of their mail app. Messages already
                    delivered are unchanged.
                  </p>
                }
                turnOffLabel="Turn off click tracking"
                refusal={clickDecision.allowed ? null : clickDecision.message}
                busy={busy || provisioning}
                onChange={async (next) => {
                  await save({ ...unchangedUpdate(settings), click: next });
                }}
              />
            </div>

            {/*
              Gated on the precondition separately from the switches above.
              Setting up a redirect subdomain is work that only pays off once
              there is a configuration set to attach it to, and offering it to
              somebody whose SES account is disconnected would send them to
              edit DNS for a feature they cannot reach. The switches survive
              that state because they are the way out of it; this does not.
            */}
            {reading.blockedBy === null && (
              <RedirectDomainPanel response={response} onChanged={onChanged} busy={busy || provisioning} />
            )}
          </>
        )}

        {/*
          VDM sits OUTSIDE the precondition gate, and that is not an oversight.
          It is an account-level Amazon setting, not a property of a
          configuration set, and hiding it behind "tracking is not set up in your
          AWS account yet" would tie a paid deliverability feature to a consent
          about recipient privacy that has nothing to do with it.

          A CORRECTION, because this comment used to promise something the
          product cannot deliver. It said a tenant who wants Amazon's own view
          and wants no pixel and no rewritten links "is entitled to exactly
          that". They are entitled to want it; they cannot have the per-provider
          panel. Wend reads VDM dimensioned by CONFIGURATION_SET — the tracking
          set — and a message is only inside the tracking set when the send named
          it, which by this slice's safety property happens only when open or
          click tracking is on. (Untracked mail goes through the delivery reports
          set since slice 19, which the panel does not read.) With both off,
          every figure comes back zero for ever.
          The panel now says so in that state (`needs_tracking_on`) instead of
          telling somebody to wait, and the panel below says it before the switch
          rather than after the bill.

          It does need an SES account, which the panel refuses for itself —
          `evaluateTrackingChange` gates `needs_ses_connection` on `open ||
          click`, so a VDM-only change with nothing connected is not refused by
          the shared evaluator. That is a hole in a shared rule being covered by
          a frontend, which is the weaker of the two places to put it, and it is
          in this slice's report for the evaluator to close.
        */}
        <VdmPanel
          response={response}
          onSave={save}
          busy={busy || provisioning}
          disabledReason={
            response.sesConnected
              ? null
              : 'Connect an Amazon SES account first. Deliverability insights are a setting inside your own AWS account, so there is nowhere to switch it on until Wend has credentials for one.'
          }
        />

        {/*
          The honesty note about the numbers themselves, on the screen that
          produces them rather than only on the screen that shows them. An open
          rate is wrong in both directions at once and never renders at the same
          visual rank as a bounce rate, which is exact.
        */}
        {reading.tracks && (
          <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
            Whatever these produce is directional and never exact. Mail apps that block images hide
            real opens; Apple Mail Privacy Protection loads the image for people who never looked;
            and the security gateways in front of a lot of corporate mail follow every link in
            every message, which can put a click rate above an open rate. Wend labels these
            everywhere they appear and never puts them beside a bounce rate as though the two were
            the same kind of number.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
